// Order Status Configuration
// Maps snake_case values from backend to user-friendly display labels

export const ORDER_STATUS_CONFIG = {
  awaiting_dropoff: {
    label: 'Awaiting dropoff',
    value: 'awaiting_dropoff',
    color: 'warning',
  },
  pending_collection: {
    label: 'Pending collection',
    value: 'pending_collection',
    color: 'warning',
  },
  collected: {
    label: 'Collected',
    value: 'collected',
    color: 'info',
  },
  out_for_delivery: {
    label: 'Out for delivery',
    value: 'out_for_delivery',
    color: 'info',
  },
  in_transit: {
    label: 'In transit',
    value: 'in_transit',
    color: 'info',
  },
  delivered: {
    label: 'Delivered',
    value: 'delivered',
    color: 'success',
  },
  finalised: {
    label: 'Finalised',
    value: 'finalised',
    color: 'success',
  },
  returned_to_sender: {
    label: 'Returned to sender',
    value: 'returned_to_sender',
    color: 'error',
  },
  cancelled: {
    label: 'Cancelled',
    value: 'cancelled',
    color: 'error',
  },
  cancelled_by_courier: {
    label: 'Cancelled by courier',
    value: 'cancelled_by_courier',
    color: 'error',
  },
  collection_exception: {
    label: 'Collection exception',
    value: 'collection_exception',
    color: 'error',
  },
  delivery_exception: {
    label: 'Delivery exception',
    value: 'delivery_exception',
    color: 'error',
  },
  failed_delivery: {
    label: 'Failed delivery',
    value: 'failed_delivery',
    color: 'error',
  },
  failed_collection: {
    label: 'Failed collection',
    value: 'failed_collection',
    color: 'error',
  },
  ready_for_pickup: {
    label: 'Ready for pickup',
    value: 'ready_for_pickup',
    color: 'info',
  },
  collect_and_return_to_hub: {
    label: 'Collect and return to hub',
    value: 'collect_and_return_to_hub',
    color: 'warning',
  },
  // Legacy statuses for backward compatibility
  pending: {
    label: 'Pending',
    value: 'pending',
    color: 'warning',
  },
  paid: {
    label: 'Paid',
    value: 'paid',
    color: 'success',
  },
  completed: {
    label: 'Completed',
    value: 'completed',
    color: 'success',
  },
  verified: {
    label: 'Verified',
    value: 'verified',
    color: 'success',
  },
  approved: {
    label: 'Approved',
    value: 'approved',
    color: 'primary',
  },
  rejected: {
    label: 'Rejected',
    value: 'rejected',
    color: 'error',
  },
};

/**
 * Get the display label for a status value
 * @param {string} statusValue - The snake_case status value from backend
 * @returns {string} The user-friendly display label
 */
export const getStatusLabel = (statusValue) => {
  if (!statusValue) return '';

  const status = ORDER_STATUS_CONFIG[statusValue.toLowerCase()];
  if (status) {
    return status.label;
  }

  // Fallback: Convert snake_case to Title Case if status not found
  return statusValue
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

/**
 * Get the color scheme for a status value
 * @param {string} statusValue - The snake_case status value from backend
 * @returns {string} The color scheme identifier
 */
export const getStatusColor = (statusValue) => {
  if (!statusValue) return 'default';

  const status = ORDER_STATUS_CONFIG[statusValue.toLowerCase()];
  return status ? status.color : 'default';
};

/**
 * Get all order status options for filters
 * @returns {Array} Array of {value, label} objects for select options
 */
export const getOrderStatusOptions = () => {
  return [
    { value: '', label: 'All Status' },
    { value: 'attention', label: '⚠ Needs attention' },
    { value: 'pending', label: 'Payment pending' },
    { value: 'awaiting_dropoff', label: 'Awaiting dropoff' },
    { value: 'pending_collection', label: 'Pending collection' },
    { value: 'collected', label: 'Collected' },
    { value: 'out_for_delivery', label: 'Out for delivery' },
    { value: 'in_transit', label: 'In transit' },
    { value: 'delivered', label: 'Delivered' },
    { value: 'finalised', label: 'Finalised' },
    { value: 'returned_to_sender', label: 'Returned to sender' },
    { value: 'cancelled', label: 'Cancelled' },
    { value: 'cancelled_by_courier', label: 'Cancelled by courier' },
    { value: 'collection_exception', label: 'Collection exception' },
    { value: 'delivery_exception', label: 'Delivery exception' },
    { value: 'failed_delivery', label: 'Failed delivery' },
    { value: 'failed_collection', label: 'Failed collection' },
    { value: 'ready_for_pickup', label: 'Ready for pickup' },
    { value: 'collect_and_return_to_hub', label: 'Collect and return to hub' },
  ];
};

/**
 * Get common order status options (shorter list for simple filters)
 * @returns {Array} Array of {value, label} objects for select options
 */
export const getCommonOrderStatusOptions = () => {
  return [
    { value: '', label: 'All Status' },
    { value: 'pending', label: 'Pending' },
    { value: 'paid', label: 'Paid' },
    { value: 'in_transit', label: 'In transit' },
    { value: 'delivered', label: 'Delivered' },
    { value: 'completed', label: 'Completed' },
    { value: 'cancelled', label: 'Cancelled' },
  ];
};

/**
 * Check if a status is a success/positive state
 * @param {string} statusValue - The snake_case status value from backend
 * @returns {boolean}
 */
export const isSuccessStatus = (statusValue) => {
  return getStatusColor(statusValue) === 'success';
};

/**
 * Check if a status is an error/negative state
 * @param {string} statusValue - The snake_case status value from backend
 * @returns {boolean}
 */
export const isErrorStatus = (statusValue) => {
  return getStatusColor(statusValue) === 'error';
};

/**
 * Check if a status is in progress
 * @param {string} statusValue - The snake_case status value from backend
 * @returns {boolean}
 */
export const isInProgressStatus = (statusValue) => {
  const color = getStatusColor(statusValue);
  return color === 'info' || color === 'warning';
};
